import { useMiranda, MirandaMessage } from '@/contexts/MirandaContext';
import { useCallback, useState, useRef, useEffect } from 'react';
import type { PendingFile } from '@/components/miranda/ChatInput';
import { authFetch } from '@/services/api';
import { toFocusPayload } from '@/lib/miranda-focus';

/**
 * Hook per gestionar el chat de Miranda
 * Versió simplificada sense useChat d'AI SDK (per compatibilitat)
 * TODO: Migrar a useChat quan l'API sigui estable
 */
export function useMirandaChat() {
  const {
    agentId,
    sessionId,
    setSessionId,
    messages,
    setMessages,
    isStreaming,
    setIsStreaming,
    updateTokenUsage,
    focus,
  } = useMiranda();

  const [input, setInput] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  
  // Ref per tenir sempre el sessionId més recent (evita problemes de closure)
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // 🆕 F2 — El focus es llegeix en el moment d'enviar, no quan es va crear el `sendMessage`:
  // amb la fitxa oberta es pot canviar de reserva a la barra de grup mentre s'escriu, i el
  // que compta és el que hi ha a l'URL quan es prem Enter.
  const focusRef = useRef(focus);
  useEffect(() => {
    focusRef.current = focus;
  }, [focus]);

  // Enviar missatge al backend
  const sendMessage = useCallback(
    async (content: string, filesToSend?: PendingFile[]) => {
      if (!content.trim() || isStreaming) return;

      // Afegir missatge de l'usuari
      const userMessage: MirandaMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);

      try {
        // `authFetch` torna la `Response` sense llegir-ne el cos. Si la resposta inicial és un
        // 401, refresca el token i repeteix la petició UN cop (el cos és text i es pot tornar a
        // enviar); un cop aquí es comença a llegir el stream ja no es repeteix res.
        const response = await authFetch(`/agent/chat/${agentId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // El cos ha de coincidir amb `ChatRequestDto` del backend: el `ValidationPipe`
          // global rebutja qualsevol camp que no hi sigui declarat.
          body: JSON.stringify({
            message: content,
            sessionId: sessionIdRef.current ?? undefined, // Usar ref per valor més recent
            files: filesToSend && filesToSend.length > 0 ? filesToSend.map(f => ({
              dataUri: f.dataUri,
              name: f.name,
              mimeType: f.mimeType,
            })) : undefined,
            // Del client només ids (`ChatFocusDto`). Cap nom, cap telèfon, cap resum: el
            // bloc `## CONTEXT ACTUAL` el munta el backend amb el rol de qui pregunta.
            // Reserva (`/reserves/…`) o conversa de WhatsApp (`/whatsapp/<id>`).
            focus: toFocusPayload(focusRef.current),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Capturar sessionId si és nova sessió
        const newSessionId = response.headers.get('X-Session-Id');
        if (newSessionId && newSessionId !== sessionIdRef.current) {
          sessionIdRef.current = newSessionId; // Actualitzar ref immediatament
          setSessionId(newSessionId); // Actualitzar context
        }

        // Processar SSE stream
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let assistantContent = '';
        const assistantMessage: MirandaMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: '',
          createdAt: new Date(),
        };

        // Afegir missatge buit de l'assistent
        setMessages((prev) => [...prev, assistantMessage]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                // Suportar múltiples formats de resposta
                const textChunk = data.text || data.textDelta;
                if (textChunk) {
                  assistantContent += textChunk;
                  // Actualitzar missatge de l'assistent
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMessage.id
                        ? { ...m, content: assistantContent }
                        : m
                    )
                  );
                }
                // Marcar com a completat si done i actualitzar tokens
                if (data.done) {
                  console.log('Stream completed', data);
                  // Actualitzar comptador de tokens si tenim usage
                  if (data.usage) {
                    const tokens = data.usage.total || (data.usage.tokensIn + data.usage.tokensOut) || 0;
                    console.log(`📊 Tokens: ${tokens} (in: ${data.usage.tokensIn}, out: ${data.usage.tokensOut})`);
                    if (tokens > 0) {
                      updateTokenUsage(tokens);
                    }
                  }
                }
              } catch {
                // Ignorar línies que no són JSON vàlid
              }
            }
          }
        }
      } catch (err) {
        console.error('Chat error:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsStreaming(false);
      }
    },
    [
      agentId,
      // sessionId s'accedeix via ref, no cal com a dependència
      setSessionId,
      setMessages,
      isStreaming,
      setIsStreaming,
      updateTokenUsage,
    ]
  );

  // Handler per form submit
  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!input.trim() && pendingFiles.length === 0) return;
      const messageText = input.trim() || pendingFiles.map(f => f.name).join(', ');
      const filesToSend = [...pendingFiles]; // Snapshot before clearing
      setInput('');
      setPendingFiles([]);
      sendMessage(messageText, filesToSend);
    },
    [input, sendMessage, pendingFiles]
  );

  // Reload (reintentar últim missatge)
  const reload = useCallback(() => {
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMessage) {
      // Eliminar últim missatge de l'assistent
      setMessages((prev) => {
        // Trobar l'últim índex de missatge assistant
        let lastAssistantIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].role === 'assistant') {
            lastAssistantIdx = i;
            break;
          }
        }
        if (lastAssistantIdx >= 0) {
          return prev.slice(0, lastAssistantIdx);
        }
        return prev;
      });
      sendMessage(lastUserMessage.content);
    }
  }, [messages, setMessages, sendMessage]);

  // Stop (cancel·lar stream)
  const stop = useCallback(() => {
    // TODO: Implementar cancel·lació real amb AbortController
    setIsStreaming(false);
  }, [setIsStreaming]);

  // File helpers
  const addFiles = useCallback((files: PendingFile[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  return {
    // Estat
    messages,
    input,
    setInput,
    isLoading: isStreaming,
    error,

    // Files
    pendingFiles,
    addFiles,
    removeFile,
    clearFiles,

    // Accions
    handleSubmit,
    sendMessage,
    reload,
    stop,

    // Metadata
    sessionId,
    agentId,
  };
}

export default useMirandaChat;
